import { useCallback, useMemo } from "react";
import moment from "moment";
import { ResponsiveLine } from "@nivo/line";
import type { CustomLayerProps, Layer, SliceTooltipProps } from "@nivo/line";
import { Show } from "shared/components";
import { Card } from "shared/components/Card";
import { Loader } from "shared/components/Loader";
import { cx } from "shared/utils";
import { useMedia } from "shared/providers/media-query/use-media";
import { formatCompactNumber, getNiceAxis, getTicks } from "../utils";
import type { ListingViewsResponse } from "api/crm-analytics";
import { AnalyticsEmptyState } from "./AnalyticsEmptyState";

type DailyPoint = ListingViewsResponse["daily"][number];

type ChartPoint = { label: string; total_views: number; unique_views: number };

const MONTHLY_THRESHOLD = 90;
const YEARLY_THRESHOLD_YEARS = 2;

const fillDateRange = (
  daily: DailyPoint[],
  startDate: string,
  endDate: string
): ChartPoint[] => {
  const start = moment(startDate).startOf("day");
  const end = moment(endDate).startOf("day");
  const totalDays = end.diff(start, "days") + 1;
  const totalYears = end.diff(start, "years", true);

  if (totalYears >= YEARLY_THRESHOLD_YEARS)
    return aggregateByYear(daily, startDate, endDate);
  if (totalDays > MONTHLY_THRESHOLD)
    return aggregateByMonth(daily, startDate, endDate);

  const lookup = new Map(daily.map((d) => [d.date, d]));
  const fmt = start.year() !== end.year() ? "MMM YYYY" : "MMM D";
  return Array.from({ length: totalDays }, (_, i) => {
    const day = start.clone().add(i, "day");
    const point = lookup.get(day.format("YYYY-MM-DD"));
    return {
      label: day.format(fmt),
      total_views: point?.total_views ?? 0,
      unique_views: point?.unique_views ?? 0
    };
  });
};

const aggregateByMonth = (
  daily: DailyPoint[],
  startDate: string,
  endDate: string
): ChartPoint[] => {
  const monthMap = new Map<
    string,
    { total_views: number; unique_views: number }
  >();
  daily.forEach((d) => {
    const key = moment(d.date).format("YYYY-MM");
    const existing = monthMap.get(key);
    monthMap.set(key, {
      total_views: (existing?.total_views ?? 0) + d.total_views,
      unique_views: (existing?.unique_views ?? 0) + d.unique_views
    });
  });

  const start = moment(startDate).startOf("month");
  const end = moment(endDate).startOf("month");
  const totalMonths = end.diff(start, "months") + 1;
  return Array.from({ length: totalMonths }, (_, i) => {
    const month = start.clone().add(i, "month");
    const agg = monthMap.get(month.format("YYYY-MM"));
    return {
      label: month.format("MMM YYYY"),
      total_views: agg?.total_views ?? 0,
      unique_views: agg?.unique_views ?? 0
    };
  });
};

const aggregateByYear = (
  daily: DailyPoint[],
  startDate: string,
  endDate: string
): ChartPoint[] => {
  const yearMap = new Map<
    string,
    { total_views: number; unique_views: number }
  >();
  daily.forEach((d) => {
    const key = moment(d.date).format("YYYY");
    const existing = yearMap.get(key);
    yearMap.set(key, {
      total_views: (existing?.total_views ?? 0) + d.total_views,
      unique_views: (existing?.unique_views ?? 0) + d.unique_views
    });
  });

  const startYear = moment(startDate).year();
  const endYear = moment(endDate).year();
  return Array.from({ length: endYear - startYear + 1 }, (_, i) => {
    const key = String(startYear + i);
    const agg = yearMap.get(key);
    return {
      label: key,
      total_views: agg?.total_views ?? 0,
      unique_views: agg?.unique_views ?? 0
    };
  });
};

type SlicePoint = {
  id: string;
  x: number;
  y: number;
  data: {
    x: string | number;
    xFormatted: string;
    y: number;
    yFormatted: string;
  };
  serieId: string;
  serieColor: string;
};

type SliceData = {
  id: string;
  x0: number;
  x: number;
  width: number;
  points: SlicePoint[];
};

const ActivePointLayer = (props: CustomLayerProps) => {
  const extended = props as CustomLayerProps & { currentSlice?: SliceData };
  const slice = extended.currentSlice;
  const isSingle = props.series[0]?.data.length <= 1;

  let points: SlicePoint[] = [];
  if (slice) points = slice.points;
  else if (isSingle) points = props.points as SlicePoint[];

  if (!points.length) return null;

  return (
    <g>
      {points.map((pt) => (
        <circle
          key={pt.id}
          cx={pt.x}
          cy={pt.y}
          r={4}
          fill={slice ? pt.serieColor : "#fff"}
          stroke={pt.serieColor}
          strokeWidth={3}
          style={{ pointerEvents: "none" }}
        />
      ))}
    </g>
  );
};

const SliceTooltip = ({ slice }: SliceTooltipProps) => (
  <div className="bg-background-1000 p-4 rounded-lg shadow-xs border border-alpha-100 text-sm">
    <p className="text-content-1000 font-medium pb-2">
      {slice.points[0].data.xFormatted}
    </p>
    {slice.points.map((p) => (
      <div
        key={p.serieId}
        className="flex items-center justify-between gap-4 py-1"
      >
        <div className="flex items-center gap-2">
          <span
            className="w-1 h-5 rounded-full"
            style={{ backgroundColor: p.serieColor }}
          />
          <span className="text-content-700">{p.serieId}</span>
        </div>
        <span className="font-bold text-content-1000">
          {Number(p.data.y).toLocaleString()}
        </span>
      </div>
    ))}
    {slice.points.length > 1 ? (
      <div className="flex justify-between pt-2 border-t border-alpha-40">
        <span className="font-medium text-content-1000">Total:</span>
        <span className="font-bold text-content-1000">
          {slice.points
            .reduce((sum, p) => sum + Number(p.data.y), 0)
            .toLocaleString()}
        </span>
      </div>
    ) : null}
  </div>
);

const getXTickValues = (labels: string[], max = 11): string[] => {
  if (labels.length <= max) return labels;
  const step = Math.ceil(labels.length / max);
  return labels.filter((_, i) => i % step === 0 || i === labels.length - 1);
};

const XAxisTick = ({
  x,
  y,
  value,
  first,
  last
}: {
  x: number;
  y: number;
  value: string | number;
  first: string;
  last: string;
}) => {
  let anchor: "start" | "middle" | "end" = "middle";
  if (first !== last && String(value) === first) anchor = "start";
  else if (first !== last && String(value) === last) anchor = "end";
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        textAnchor={anchor}
        dominantBaseline="central"
        style={{ fontSize: 12, fill: "#8B8D97" }}
        dy={16}
      >
        {String(value)}
      </text>
    </g>
  );
};

const CHART_THEME = {
  axis: {
    domain: { line: { stroke: "#F1F3F9", strokeWidth: 1 } },
    ticks: {
      line: { stroke: "transparent", strokeWidth: 0 },
      text: { fontSize: 12, fill: "#8B8D97" }
    }
  },
  tooltip: {
    container: {
      background: "#ffffff",
      fontSize: 12,
      padding: 0,
      boxShadow: "0px 4px 32px 0px #8D1D5A0F"
    }
  },
  grid: {
    line: { stroke: "#F1F3F9", strokeWidth: 1, strokeDasharray: "4 4" }
  },
  crosshair: {
    line: { stroke: "#C8CAD0", strokeWidth: 1 }
  }
} as const;

const CHART_LAYERS: Layer[] = [
  "grid",
  "markers",
  "axes",
  "areas",
  "crosshair",
  "lines",
  "slices",
  ActivePointLayer,
  "legends"
];

export const ListingViewsCard = ({
  data,
  isLoading,
  color,
  startDate,
  endDate
}: {
  data: ListingViewsResponse | undefined;
  isLoading: boolean;
  color: string;
  startDate: string;
  endDate: string;
}) => {
  const { isMobile } = useMedia();
  const hasData = Boolean(data?.daily && data.daily.length > 0);
  const showEmptyState = !isLoading && !hasData;
  const showChart = !isLoading && hasData;
  const chartPoints = useMemo(
    () =>
      data?.daily?.length
        ? fillDateRange(data.daily, startDate, endDate)
        : undefined,
    [data?.daily, startDate, endDate]
  );

  const chartData = useMemo(() => {
    if (!chartPoints?.length) return [];
    return [
      {
        id: "Boom Listings",
        data: chartPoints.map((d) => ({ x: d.label, y: d.total_views }))
      }
    ];
  }, [chartPoints]);

  const maxY = useMemo(() => {
    if (!chartPoints?.length) return 0;
    return Math.max(...chartPoints.map((d) => d.total_views));
  }, [chartPoints]);

  const { axisMax, tickCount } = getNiceAxis(maxY);
  const gridYValues = getTicks(axisMax, tickCount);
  const xLabels = chartData[0]?.data.map((d) => String(d.x)) ?? [];
  const xTickValues = getXTickValues(xLabels, isMobile ? 5 : 11);

  const firstTick = xTickValues[0];
  const lastTick = xTickValues[xTickValues.length - 1];
  const renderTick = useCallback(
    (props: { x: number; y: number; value: string | number }) => (
      <XAxisTick {...props} first={firstTick} last={lastTick} />
    ),
    [firstTick, lastTick]
  );

  return (
    <Card className="p-6 w-full h-full">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <p className="text-base font-medium text-content-700">
            Listing views
          </p>
          <p className="text-2xl font-bold text-content-1000">
            {data?.total_views ?? "-"}
          </p>
        </div>
        <Show if={hasData}>
          <div className="flex items-center gap-2">
            <span
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: color }}
            />
            <span className="text-xs font-medium text-content-1000">
              Boom Listings
            </span>
          </div>
        </Show>
      </div>

      <Show if={isLoading}>
        <div className="flex items-center justify-center min-h-[120px]">
          <Loader size={24} />
        </div>
      </Show>

      <Show if={showEmptyState}>
        <AnalyticsEmptyState description="There are no data to show for the selected filter" />
      </Show>

      <Show if={showChart}>
        <div className={cx("mt-6", isMobile ? "overflow-x-auto" : "")}>
          <div className={cx("h-[300px]", isMobile ? "min-w-[500px]" : "")}>
            <ResponsiveLine
              data={chartData}
              theme={CHART_THEME}
              margin={{ top: 20, right: 20, bottom: 40, left: 50 }}
              xScale={{ type: "point" }}
              yScale={{ type: "linear", min: 0, max: axisMax, stacked: false }}
              yFormat=" >-.0f"
              curve="monotoneX"
              lineWidth={2}
              enableArea
              areaOpacity={0.08}
              axisBottom={{
                tickSize: 0,
                tickPadding: 10,
                tickValues: xTickValues,
                renderTick
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 10,
                tickValues: gridYValues,
                format: (v) => formatCompactNumber(Number(v))
              }}
              gridYValues={gridYValues}
              colors={[color]}
              enableGridX={false}
              enablePoints={false}
              enableSlices="x"
              sliceTooltip={SliceTooltip}
              layers={CHART_LAYERS}
            />
          </div>
        </div>
      </Show>
    </Card>
  );
};
